import {Component, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import Cookies from 'js-cookie';
import Header from '@/header';
import Footer from '@/footer';
import {withMarginTopAdBanner} from '@/hocs/withSpacing';
import {
  AD_BANNER_DISMISSED_COOKIE,
  AD_BANNER_COOKIE_EXPIRES_DAYS
} from 'helpers/ad-banner';

const {NEXT_PUBLIC_NOINDEX = 'false'} = process.env;
const assetVersion = process.env.ASSET_VERSION || 'defaultVersion';
const defaultOgImage = `https://www.workable.com/static/images/workable_meta_image.jpg?v=${assetVersion}`;

const propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  canonical: PropTypes.string.isRequired,
  ogtitle: PropTypes.string,
  ogdescription: PropTypes.string,
  ogimage: PropTypes.string,
  lightHeader: PropTypes.bool,
  withFaq: PropTypes.object,
  noIndexMeta: PropTypes.bool,
  hideAdBanner: PropTypes.bool
};

const DivMarginTopActiveBanner = withMarginTopAdBanner('div');

const MainLayout = ({
  children,
  title,
  description,
  canonical,
  ogtitle = title,
  ogdescription = description,
  ogimage = defaultOgImage,
  lightHeader = false,
  withFaq,
  noIndexMeta,
  hideAdBanner = false // Set to true to hide the ad banner site-wide.
}) => {
  // Default to false so banner + spacer are in server-rendered HTML (avoids CLS, works without JS).
  // AdBannerDismissHead in _document sets data-ad-banner-dismissed before paint for returning visitors.
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const dismissed = Cookies.get(AD_BANNER_DISMISSED_COOKIE);
    setBannerDismissed(!!dismissed);
  }, []);

  const showAdBanner = !hideAdBanner && !bannerDismissed;

  const onBannerDismiss = () => {
    Cookies.set(AD_BANNER_DISMISSED_COOKIE, 'true', {
      expires: AD_BANNER_COOKIE_EXPIRES_DAYS,
      path: '/'
    });
    setBannerDismissed(true);
  };

  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <title>{title}</title>
        <meta content={description} name="description" />
        <link href={canonical} rel="canonical" />
        <meta content={ogtitle} property="og:title" />
        <meta content={ogdescription} property="og:description" />
        <meta content={ogimage} property="og:image" />
        <meta content="website" property="og:type" />
        {(noIndexMeta || NEXT_PUBLIC_NOINDEX === 'true') && <meta name="robots" content="noindex" />}
        {withFaq}
      </Head>
      <Header
        lightHeader={lightHeader}
        showAdBanner={showAdBanner}
        onBannerDismiss={onBannerDismiss}
      />
      {showAdBanner ? (
        <div data-ad-banner-spacer>
          <DivMarginTopActiveBanner />
        </div>
      ) : null}
      {children}
      <Footer />
    </>
  );
};

MainLayout.propTypes = propTypes;

/**
 * Resolves SEO metadata - handles both static objects and dynamic functions
 * @param {Object|Function} metas - Either a static metadata object or a function that returns metadata
 * @param {Object} props - Component props to pass to the function if metas is a function
 * @returns {Object} Resolved metadata object
 */
function resolveMetas(metas, props) {
  if (typeof metas === 'function') {
    return metas(props);
  }
  return metas;
}

// Built using React HOCs
const Main = (
  Page,
  metas = {
    title: 'The leading Applicant Tracking System & Recruiting Software | Workable',
    description: `More than an Applicant Tracking System, Workable's Recruiting Software improves the way teams find, evaluate and hire candidates.`,
    canonical: 'https://www.workable.com/',
    ogimage: defaultOgImage
  }
) => {
  // eslint-disable-next-line react/prefer-stateless-function
  const WrappedPage = class extends Component {
    render() {
      const resolvedMetas = resolveMetas(metas, this.props);
      return (
        <MainLayout {...resolvedMetas}>
          <Page {...this.props} />
        </MainLayout>
      );
    }
  };

  return WrappedPage;
};

export default Main;
